import React from 'react';
import { DatePickerInput } from '@mantine/dates';
import { twMerge } from 'tailwind-merge';
import { customDefaultRange, fromISODate, RangeFilter, RangePreset, toISODate, todayUTC } from './dateRange';

// Page-level date range control. Presets cover the common cases; Custom opens
// two date pickers, prefilled with the last 14 days the first time. A picker
// takes no typed text: the value comes from the calendar or the clear button.
// It always shows YYYY-MM-DD, whatever the browser locale. Height matches the
// other filters via className.
const controlClass = 'h-[34px] bg-body-bg border border-gray-600 rounded-md';

// Mantine's stylesheet loads after Tailwind, so the input needs !important.
// Callers that pass a height must use !h-* too. The width fits YYYY-MM-DD
// plus the clear button.
const dateInputClass =
  '!h-[34px] !min-h-0 !bg-body-bg !border-gray-600 !rounded-md !pl-3 !text-sm !text-body-color focus:!border-primary aria-expanded:!border-primary';
const dateRootClass = 'w-[8.5rem]';

function DateRangeFilter({
  value,
  onChange,
  className,
}: {
  value: RangeFilter;
  onChange: (range: RangeFilter) => void;
  className?: string;
}) {
  const set = (patch: Partial<RangeFilter>) => onChange({ ...value, ...patch });

  const selectPreset = (preset: RangePreset) => {
    if (preset === 'custom' && !value.from && !value.to) {
      set({ preset, ...customDefaultRange(todayUTC()) });
      return;
    }
    set({ preset });
  };

  const presetBtn = (preset: RangePreset, label: string) => (
    <button
      key={preset}
      type="button"
      aria-pressed={value.preset === preset}
      onClick={() => selectPreset(preset)}
      className={`h-full px-3 text-sm rounded transition-colors ${
        value.preset === preset ? 'bg-primary/20 text-primary' : 'text-secondary hover:text-body-color'
      }`}
    >
      {label}
    </button>
  );

  const dateInput = (field: 'from' | 'to', label: string) => (
    <DatePickerInput
      aria-label={label}
      placeholder="YYYY-MM-DD"
      valueFormat="YYYY-MM-DD"
      clearable
      clearButtonProps={{ 'aria-label': `Clear ${label.toLowerCase()}` }}
      value={fromISODate(value[field])}
      onChange={(d) => set({ [field]: d ? toISODate(d) : '' })}
      classNames={{ root: dateRootClass, input: twMerge(dateInputClass, className) }}
    />
  );

  return (
    <>
      <div
        role="group"
        aria-label="Date range"
        className={twMerge(controlClass, 'flex items-center gap-0.5 p-0.5', className)}
      >
        {presetBtn('7d', '7d')}
        {presetBtn('30d', '30d')}
        {presetBtn('custom', 'Custom')}
      </div>
      {value.preset === 'custom' && (
        <>
          {dateInput('from', 'From date')}
          {dateInput('to', 'To date')}
        </>
      )}
    </>
  );
}

export default DateRangeFilter;
